import React from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import { Button, message, notification } from 'antd';
import type { MenuDataItem } from '@ant-design/pro-layout';

import type { SearchMenu } from './app';
import IconFont from './components/IconFont';
import defaultSettings from '../config/defaultSettings';
import { queryUserPermissionMenu } from './pages/System/RoleManage/service';
import IntlMessage from './components/IntlMessage';
import {getLocale} from "@@/plugin-locale/localeExports";

const { pwa } = defaultSettings;
const isHttps = document.location.protocol === 'https:';

// if pwa is true
if (pwa) {
  // Notify user if offline now
  window.addEventListener('sw.offline', () => {
    message.warning(useIntl().formatMessage({ id: 'app.pwa.offline' }));
  });

  // Pop up a prompt on the page asking the user if they want to use the latest version
  window.addEventListener('sw.updated', (event: Event) => {
    const e = event as CustomEvent;
    const reloadSW = async () => {
      // Check if there is sw whose state is waiting in ServiceWorkerRegistration
      // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration
      const worker = e.detail && e.detail.waiting;
      if (!worker) {
        return true;
      }
      // Send skip-waiting event to waiting SW with MessageChannel
      await new Promise((resolve, reject) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = (msgEvent) => {
          if (msgEvent.data.error) {
            reject(msgEvent.data.error);
          } else {
            resolve(msgEvent.data);
          }
        };
        worker.postMessage({ type: 'skip-waiting' }, [channel.port2]);
      });
      // Refresh current page to use the updated HTML and other assets after SW has skiped waiting
      window.location.reload(true);
      return true;
    };
    const key = `open${Date.now()}`;
    const btn = (
      <Button
        type="primary"
        onClick={() => {
          notification.close(key);
          reloadSW();
        }}
      >
        {useIntl().formatMessage({ id: 'app.pwa.serviceworker.updated.ok' })}
      </Button>
    );
    notification.open({
      message: useIntl().formatMessage({ id: 'app.pwa.serviceworker.updated' }),
      description: useIntl().formatMessage({ id: 'app.pwa.serviceworker.updated.hint' }),
      btn,
      key,
      onClose: async () => null,
    });
  });
} else if ('serviceWorker' in navigator && isHttps) {
  // unregister service worker
  const { serviceWorker } = navigator;
  if (serviceWorker.getRegistrations) {
    serviceWorker.getRegistrations().then((sws) => {
      sws.forEach((sw) => {
        sw.unregister();
      });
    });
  }
  serviceWorker.getRegistration().then((sw) => {
    if (sw) sw.unregister();
  });

  // remove all caches
  if (window.caches && window.caches.keys) {
    caches.keys().then((keys) => {
      keys.forEach((key) => {
        caches.delete(key);
      });
    });
  }
}

// 国际化
window.localeList = [];
window.$tc = (key: string, fn) => {
  const localeProxy = new Proxy(fn, {
    apply(target, ctx, args) {
      if (!window.localeList.includes(args?.[0]?.id)) {
        window.localeList.push(args?.[0]?.id);
      }
      return Reflect.apply(...arguments);
    }
  })
  return (id: string, defaultMessage?: string, params?: Record<string, string | number>) => {
    return localeProxy({id: (key ? key + '.' : '') + id, defaultMessage}, params)
  }
}
export const i18nMap = {
	'zh-CN': 'zh',
	'zh-TW': 'zh',
	'en-US': 'en',
}
// console.log('修改moment')
const locale = getLocale() === 'zh-CN' ? 'zh' : 'en';
moment.locale(locale);

export const fetchPermissionMenus = async () => {
  try {
    const { data: menus } = await queryUserPermissionMenu();
    console.log('%c================= 已获取新路由数据 =================', 'color:red')
    return menus;
  } catch (err) {
    console.log(err);
  }
  return [];
}

export type MenuLayout = {
  layout?: boolean;
}

/** moment.js 插件国际化值转换
 * @ en-US => en
 * @ zh-CN => zh-cn
*/
export const momentLocales = {
  'en-US': 'en',
  'zh-CN': 'zh-cn',
}

export type HandleType = 'ADD' | 'EDIT';
export type HandleType2 = 'ADD' | 'EDIT' | 'LOOK';

export const OperationTypeEnum = {
  'ADD': <IntlMessage id="common.operation.add" msg="添加" />,
  'NEW': <IntlMessage id="common.operation.new" msg="新增" />,
  'EDIT': <IntlMessage id="common.operation.edit" msg="编辑" />,
  'VIEW': <IntlMessage id="common.operation.view" msg="查看" />,
  'COPY': <IntlMessage id="common.operation.copy" msg="复制" />,
};
export type OperationType = keyof typeof OperationTypeEnum;
